import { create } from 'zustand';
import { FEEDBACK_MILESTONES, type FeedbackMilestone } from '../models/feedback';
import { FEEDBACK_DONE_KEY, PLAY_SECONDS_KEY, read, write } from './preferences';

/** A tick adds at most this much play time, however long it was since the last one. */
export const MAX_TICK_MS = 5000;
/** Play time is written to the device at most this often while counting. */
export const PERSIST_EVERY_MS = 15000;

let clock: () => number = () => Date.now();
/** Tests: replace the clock (milliseconds). */
export function setPlayClock(fn: () => number) {
  clock = fn;
}

const readInt = (key: string) => {
  const n = Number.parseInt(read(key) ?? '', 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

interface PlayTimeState {
  /** Whole seconds of active play on this device. */
  seconds: number;
  /** Start counting from now. */
  resume: () => void;
  /** Stop counting and save the total. */
  pause: () => void;
  /** Add the time since the previous tick, capped at MAX_TICK_MS. The first tick after `resume` only sets the starting point. */
  tick: () => void;
  /** The feedback milestone now due, or null. Taking it marks it handled at once (and every smaller one with it). */
  takeDueMilestone: () => FeedbackMilestone | null;
}

// Millisecond bookkeeping lives outside the store: only `seconds` is observable.
let ms = 0;
let persistedMs = 0;
let lastTickMs: number | null = null;
let handled = 0;

function load() {
  ms = readInt(PLAY_SECONDS_KEY) * 1000;
  persistedMs = ms;
  lastTickMs = null;
  handled = readInt(FEEDBACK_DONE_KEY);
  return { seconds: Math.floor(ms / 1000) };
}

function persist() {
  persistedMs = ms;
  write(PLAY_SECONDS_KEY, String(Math.floor(ms / 1000)));
}

export const usePlayTime = create<PlayTimeState>((set, get) => ({
  ...load(),
  resume: () => {
    lastTickMs = clock();
  },
  pause: () => {
    lastTickMs = null;
    persist();
  },
  tick: () => {
    const now = clock();
    const last = lastTickMs;
    lastTickMs = now;
    if (last == null) return;
    const elapsed = Math.min(Math.max(now - last, 0), MAX_TICK_MS);
    if (elapsed === 0) return;
    ms += elapsed;
    set({ seconds: Math.floor(ms / 1000) });
    if (ms - persistedMs >= PERSIST_EVERY_MS) persist();
  },
  takeDueMilestone: () => {
    const [first, second] = FEEDBACK_MILESTONES;
    const seconds = get().seconds;
    let due: FeedbackMilestone | null = null;
    if (seconds >= second * 60 && handled < second) due = second;
    else if (seconds >= first * 60 && handled < first) due = first;
    if (due != null) {
      handled = due;
      write(FEEDBACK_DONE_KEY, String(due));
    }
    return due;
  },
}));

/** Tests: re-read the device store, as a page reload would. */
export function resetPlayTime() {
  usePlayTime.setState(load());
}
